
import Authenticator from '@/Services/Auth/Authenticator';
import { NewsService } from '@/Services/News/NewsService';
import OptimizelyService from '@/Services/OptimizelyService';
import { User } from '@/classes/auth';
import Dismiss from '@/components/Dismiss.vue';
import Icon from '@/components/Icons/Icon.vue';
import { SupportModalMixin } from '@/mixins/SupportModalMixin';
import store from '@/store';
import { AuthState } from '@/store/auth';
import { LayoutState } from '@/store/layout';
import { TranslateResult } from 'vue-i18n';
import { Component, Watch } from 'vue-property-decorator';
import { mapActions, mapState } from 'vuex';

@Component({
  components: {
    Icon,
    Dismiss,
  },
  computed: {
    ...mapState<LayoutState>('layout', {
      drawer: (state: LayoutState) => state.drawer,
      isNavigationMobile: (state: LayoutState) => state.isNavigationMobile,
      globalLoader: (state: LayoutState) => state.globalLoader,
    }),
    ...mapState<AuthState>('auth', {
      user: (state: AuthState) => state.user,
    }),
  },
  methods: {
    ...mapActions('layout', ['setDrawerState']),
  },
})
export default class AppBar extends SupportModalMixin {
  @Watch('$route.path')
  onChangeRoute(): void {
    // Close menu on page change
    this.showTooltipAccount = false;
  }

  drawer!: boolean;
  globalLoader!: boolean;
  isNavigationMobile!: boolean;
  showTooltipNews = false;
  showTooltipAccount = false;
  user!: User;
  newsService: NewsService = new NewsService();
  newsObject: { items: any } = { items: null };
  id = store?.state?.account?.account?.id;
  qrcodes = store?.state?.account?.account?.qrcodes;
  newsOffset = 0;
  closeOnContentClick = false;
  navItems: Array<{ href: string; title: string; icon: string }> = [
    {
      href: '/my-account',
      title: this.$t('fields.navigation.my_account') as string,
      icon: 'account-system',
    },
    {
      href: '/my-account/user-management',
      title: this.$t('fields.navigation.user_management') as string,
      icon: 'users-system',
    },
    {
      href: '/my-account/white-label',
      title: this.$t('fields.navigation.whitelabel_options') as string,
      icon: 'palette-system',
    },
    {
      href: '/my-account/api-keys',
      title: this.$t('fields.navigation.api_keys') as string,
      icon: 'gear-system',
    },
    {
      href: '/features-and-pricing',
      title: this.$t('fields.navigation.features_and_pricing') as string,
      icon: 'price-round-system',
    },
    {
      href: 'https://help.qr-code-generator.com/en',
      title: this.$t('fields.navigation.help_center') as string,
      icon: 'help-round-system',
    },
  ];

  setDrawerState!: (isOpen: boolean) => any;
  isPauseMode = false;

  mounted(): void {
    this.getNews(0);

    this.setIsPauseMode();
  }

  async goBack(goBackBy: number = -1): Promise<void> {
    if (this.$route.query.trial === 'expired') {
      OptimizelyService.trackEvent('pricing_page_my_qr_codes_back_button');
    } else {
      OptimizelyService.trackEvent('pricing_page_back_button');
    }
    if (this.canGoBack) {
      await this.$store.commit('app/LOCATION_HISTORY_REMOVE');
      await this.$router.go(goBackBy); // Navigate back by X steps
    } else {
      await this.$router.push({ name: 'index' }); // If we do not have any history in app fallback to homepage
    }
  }

  get backButtonLabel(): string | TranslateResult {
    return this.$route.query.trial === 'expired'
      ? this.$t('fields.navigation.my_qr_codes')
      : this.$t('fields.navigation.back');
  }

  get canGoBack(): boolean {
    return this.$store.state.app.location.history.length > 1;
  }

  /**
   * Click on the load-more button in the news
   */
  clickNewsLoadMore(): void {
    this.newsOffset += 3;
    this.getNews(this.newsOffset);
  }

  /**
   * Click on the news-button
   */
  clickNewsButton(): void {
    this.showTooltipNews = !this.showTooltipNews;
    this.markNewsAsRead();
  }

  /**
   * Toggles the state of the drawer (sidebar) via emit, which is watched in Mainframe
   */
  toggleDrawer(): void {
    this.setDrawerState(!this.drawer);
  }

  /**
   * Gets news from api
   *
   * @param offset
   */
  getNews(offset: number): void {
    this.newsService.getNews(offset).then((news) => {
      if (offset > 0 && this.newsObject.items instanceof Array) {
        this.newsObject.items.push(...news.items);
      } else {
        this.newsObject = news;
      }
    });
  }

  /**
   * Formats the given date to "JAN 01, 2020" for example
   */
  formatNewsDate(date: string): string | undefined {
    // create date-format
    let formattedDate = new Date(date.replace(/-/g, '/'));

    // split by whitespace
    let splitDateArray = formattedDate.toDateString().split(' ');

    // if is array, we create the date-string which we can use
    if (splitDateArray.length >= 4) {
      return splitDateArray[1].toUpperCase() + ' ' + splitDateArray[2] + ', ' + splitDateArray[3];
    }
  }

  /**
   * Marks the news as read
   */
  markNewsAsRead(): void {
    this.newsService.markNewsAsRead().then(() => {
      this.newsService.getNews();
    });
  }

  /**
   * Calls Authenticator class to log the user out
   */
  logout(): void {
    Authenticator.logout();
  }

  /**
   * Returns if current route is part of checkout-process.
   * In checkout we hide some elements in the app-bar.
   * @see Navigation.vue
   */
  isCheckout(routeName: string): boolean {
    let checkoutRoutes = [
      'checkout_payment_form',
      'checkout_plan_selection',
      'checkout_payment_complete',
      'my_account',
      'my_account_tab',
      'cancel_subscription',
      'checkout_payment_form_preselect_plan',
      'checkout_payment_form_preselect_country',
      'checkout_payment_form_preselect_country_and_plan',
      'checkout_payment_complete',
    ];

    return checkoutRoutes.indexOf(routeName) > -1;
  }

  // Sets the pause-mode if the url-parameter is set
  setIsPauseMode(): void {
    this.isPauseMode = this.$route.query.pausemode === 'true';
  }

  // Check if link is external or internal
  returnNavItemProps({ href }: { href: string }): { href: string } | { to: string } {
    if (href.startsWith('http://') || href.startsWith('https://')) {
      return {
        href: href,
      };
    } else {
      return {
        to: href,
      };
    }
  }
}
